// Angular
import { Inject, Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Local library
import { CommonFacade, ENVIRONMENT } from '@amwager/data-access';
import { MatNotificationService } from '@amwager/util';

// Local file
import { FundingFacade, IAccountACH } from '../+state';
import { FundingService } from './funding.service';

/** Business logic for the deposit online-check "add account" component. */
@Injectable()
export class DepositAddAccountService {

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly fundingFacade: FundingFacade,
    private readonly commonFacade: CommonFacade,
    private readonly matNotificationService: MatNotificationService,
    private readonly fundingService: FundingService,
    @Inject(ENVIRONMENT) private readonly environment: string,
  ) {}

  /** Build the add-account form and wire up its field validators. */
  buildForm(): FormGroup {
    const form = this.formBuilder.group({
      bankName: ['', [Validators.required]],
      routingName: ['', [Validators.required]],
      accountType: ['', [Validators.required]],
      accountNumber: ['', [Validators.required]]
    });

    form.get('accountType')?.setValue('PC');

    this.attachFieldValidators(form);

    return form;
  }

  /** Routing/account number character + length validation. */
  private attachFieldValidators(form: FormGroup): void {
    form.get('routingName')?.valueChanges.subscribe((value) => {
      if (value) {
        const isNumeric = /^[0-9]*$/.test(value);
        if (!isNumeric) {
          form.get('routingName')?.setErrors({ invalidCharacters: true });
        }
        else if (value.toString().length < 9 || value.toString().length > 9) {
          form.get('routingName')?.setErrors({ maxlength: true });
        }
      }
    });

    form.get('accountNumber')?.valueChanges.subscribe((value) => {
      if (value) {
        const isNumeric = /^[0-9]*$/.test(value);
        if (!isNumeric) {
          form.get('accountNumber')?.setErrors({ invalidCharacters: true });
        }
        // value.toString().length > 6
        else if (value.toString().length < 6) {
          form.get('accountNumber')?.setErrors({ minlength: true });
        }
        else if (value.toString().length > 20) {
          form.get('accountNumber')?.setErrors({ maxlength: true });
        }
      }
    });
  }

  /** Current TMS id stream. */
  tmsId$() {
    return this.commonFacade?.tmsId$();
  }

  /** Dispatch add-account and stream the result. */
  addAccount$(accountACH: IAccountACH) {
    this.fundingFacade.addAccountACH(accountACH);
    return this.fundingFacade.isAddAchAccountSuccess$();
  }

  /** Refresh the saved ACH accounts. */
  refreshAccounts(): void {
    this.fundingFacade.getAchAccount();
  }

  /** Flag the app as offline for the given source. */
  setOffline(source: string): void {
    this.commonFacade.setIsConnectedInternet(source, false);
  }

  /** Show a single-button alert. */
  showAlert(header: string, message: string): void {
    this.matNotificationService.showAlert(header, message, undefined, undefined, 'Close');
  }

  /** Watch connectivity and report changes back to the caller. */
  checkConnectivity(source: string, onChange: (online: boolean) => void): void {
    this.fundingService.checkConnectivity(this.environment, source, onChange);
  }

}
